import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { auth } from '../../firebase';

const BulkRefundDetail = () => {
    const { outageId } = useParams();
    const navigate = useNavigate();
    const [outage, setOutage] = useState(null);
    const [targets, setTargets] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [executing, setExecuting] = useState(false);

    const fetchData = async () => {
        try {
            const [outageRes, targetsRes] = await Promise.all([
                apiClient.get(`/v1/admin/bulk-refunds/${outageId}`),
                apiClient.get(`/v1/admin/bulk-refunds/${outageId}/targets?limit=1000`)
            ]);
            if (outageRes.ok) setOutage(await outageRes.json());
            if (targetsRes.ok) {
                const data = await targetsRes.json();
                setTargets(data.targets);
                setTotalCount(data.totalCount);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [outageId]);

    const handleExecute = async () => {
        if (!window.confirm(`${totalCount}社への返金予約（次回請求時の精算）を開始します。よろしいですか？`)) return;
        setExecuting(true);
        try {
            const res = await apiClient.post(`/v1/admin/bulk-refunds/${outageId}/execute`);
            if (res.ok) {
                alert('処理を開始しました。完了まで時間がかかる場合があります。');
                fetchData();
            } else {
                alert('実行に失敗しました。');
            }
        } catch (error) {
            console.error(error);
            alert('エラーが発生しました。');
        } finally {
            setExecuting(false);
        }
    };

    const handleDownloadCsv = async () => {
        try {
            // URLを直接開くのではなく、IDトークンを取得して付与した上で fetch を行う
            const user = auth.currentUser;
            if (!user) {
                alert('認証が必要です。');
                return;
            }
            const token = await user.getIdToken();
            const url = `${process.env.REACT_APP_API_URL || ''}/v1/admin/bulk-refunds/${outageId}/targets/csv?compensationDays=${outage.compensationDays}`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', `bulk_refund_targets_${outageId}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                alert('CSVの取得に失敗しました。');
            }
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('ダウンロード中にエラーが発生しました。');
        }
    };

    if (loading) return <div className="p-8 text-center">読み込み中...</div>;
    if (!outage) return <div className="p-8 text-center">データが見つかりません。</div>;

    const summary = targets.reduce((acc, t) => ({
        amountEx: acc.amountEx + t.amountEx,
        tax: acc.tax + t.tax,
        total: acc.total + t.total
    }), { amountEx: 0, tax: 0, total: 0 });

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-2xl font-bold">{outage.name}</h2>
                    <p className="text-gray-500">障害ID: {outage.outageId}</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => navigate('/admin/bulk-refunds')} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">戻る</button>
                    <button onClick={handleDownloadCsv} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">CSVダウンロード</button>
                    {outage.status === 'draft' && (
                        <button 
                            onClick={handleExecute} 
                            disabled={executing || totalCount === 0}
                            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-bold disabled:bg-gray-400"
                        >
                            {executing ? '実行中...' : '一括返金を実行する'}
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 space-y-6">
                    {/* 基本情報 */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4 border-b pb-2">障害・補償内容</h3>
                        <dl className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <dt className="text-gray-500">障害期間</dt>
                                <dd>{new Date(outage.outageStartAt).toLocaleString()} 〜 {new Date(outage.outageEndAt).toLocaleString()}</dd>
                            </div>
                            <div>
                                <dt className="text-gray-500">補償日数</dt>
                                <dd className="font-bold">{outage.compensationDays} 日</dd>
                            </div>
                            <div>
                                <dt className="text-gray-500">計算方法</dt>
                                <dd>{outage.calculationType === 'auto' ? '自動計算' : `手動指定 (${outage.manualAmountEx}円)`}</dd>
                            </div>
                            <div>
                                <dt className="text-gray-500">対象プラン</dt>
                                <dd className="uppercase">{outage.targetPlans.join(', ')}</dd>
                            </div>
                            <div className="col-span-2">
                                <dt className="text-gray-500">返金明細名</dt>
                                <dd className="bg-gray-50 p-2 rounded mt-1">{outage.invoiceDetail}</dd>
                            </div>
                        </dl>
                    </div>

                    {/* 対象企業一覧 */}
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-gray-700">返金対象企業 ({totalCount}社)</h3>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs text-gray-500">企業名 (billwaveAccounts)</th>
                                        <th className="px-4 py-2 text-left text-xs text-gray-500">プラン</th>
                                        <th className="px-4 py-2 text-right text-xs text-gray-500">返金額(税別)</th>
                                        <th className="px-4 py-2 text-right text-xs text-gray-500">税込合計</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {targets.map((t, idx) => (
                                        <tr key={idx} className="text-sm">
                                            <td className="px-4 py-2">
                                                <div className="font-medium text-gray-900">{t.companyName}</div>
                                                <div className="text-xs text-gray-500 font-mono">{t.tenantId}</div>
                                            </td>
                                            <td className="px-4 py-2 uppercase">{t.plan}</td>
                                            <td className="px-4 py-2 text-right">{t.amountEx.toLocaleString()}円</td>
                                            <td className="px-4 py-2 text-right font-bold">{t.total.toLocaleString()}円</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* 実行サマリー */}
                <div className="space-y-6">
                    <div className="bg-white shadow rounded-lg p-6 border-t-4 border-indigo-600">
                        <h3 className="text-lg font-bold mb-4">返金予定総計</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-500">対象企業数</span>
                                <span className="font-bold">{totalCount} 社</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">返金総額 (税別)</span>
                                <span className="font-bold">{summary.amountEx.toLocaleString()}円</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">消費税合計</span>
                                <span className="font-bold">{summary.tax.toLocaleString()}円</span>
                            </div>
                            <div className="flex justify-between border-t pt-3 text-lg">
                                <span className="font-bold text-red-600">返金総額 (税込)</span>
                                <span className="font-bold text-red-600">{summary.total.toLocaleString()}円</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4">ステータス: {outage.status === 'draft' ? '準備完了' : outage.status}</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>成功（予約済）:</span>
                                <span className="text-green-600 font-bold">{outage.refundedCount}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>失敗:</span>
                                <span className="text-red-600 font-bold">{outage.failedCount}</span>
                            </div>
                            {outage.totalTargetCount > 0 && (
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                                    <div 
                                        className="bg-indigo-600 h-2 rounded-full" 
                                        style={{ width: `${(outage.refundedCount / outage.totalTargetCount) * 100}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkRefundDetail;
